import { useState, useCallback } from 'react';

interface ChatPayload {
  message?: string;
  isAuthenticated: boolean;
  userEmail?: string;
  confirmFeedback?: boolean;
  feedbackText?: string;
  conversationContext?: string;
  pageUrl?: string;
  ticketKind?: 'feedback' | 'problem';
}

export type SendChatOptions = {
  /** `null` omits email (anonymous). A string includes that email. `undefined` does not add a session email. */
  overrideUserEmail?: string | null;
  confirmFeedback?: boolean;
  feedbackText?: string;
  conversationContext?: string;
  ticketKind?: 'feedback' | 'problem';
};

const getPageUrl = () => {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.pathname}${window.location.search}`;
};

export const useChatBot = (isAuthenticated: boolean, _userEmail: string | null = null) => {
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = useCallback(
    async (
      messageWithContext: string,
      options?: SendChatOptions,
    ): Promise<{
      message: string;
      isFeedback?: boolean;
      requiresEmail?: boolean;
      ticketSent?: boolean;
      ticketKind?: 'feedback' | 'problem' | null;
    } | null> => {
      setIsLoading(true);
      try {
        const payload: ChatPayload = {
          isAuthenticated: isAuthenticated || false,
          pageUrl: getPageUrl(),
        };

        if (options?.confirmFeedback) {
          payload.confirmFeedback = true;
          payload.feedbackText = options.feedbackText || messageWithContext;
          if (options.conversationContext) {
            payload.conversationContext = options.conversationContext;
          }
          if (options.ticketKind) {
            payload.ticketKind = options.ticketKind;
          }
        } else {
          payload.message = messageWithContext;
        }

        if (typeof options?.overrideUserEmail === 'string' && options.overrideUserEmail) {
          payload.userEmail = options.overrideUserEmail;
        }

        const response = await fetch(`${process.env.REACT_APP_CHATBOT_API_URL}/chatbot`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error('Network error');

        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Error sending message:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated],
  );

  return {
    isLoading,
    sendMessage,
  };
};
